import React, { useState, useEffect } from "react";
import api from "../api/homeapi";
import PublicNav from "../components/PublicNav";
import { InlineAlert } from "../components/Notify";

const C = {
  green:    "#004D2B",
  greenMid: "#006B3C",
  greenLt:  "#E6F2EC",
  gold:     "#B8933A",
  cream:    "#F9F6F1",
  white:    "#FFFFFF",
  text:     "#1A1A1A",
  sub:      "#6B6B6B",
  border:   "#E2DDD7",
};

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,500;0,700;1,300;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'DM Sans', sans-serif; background: ${C.cream}; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    from { transform: translateX(-100%); }
    to   { transform: translateX(100%); }
  }
  .nav-link { color: rgba(255,255,255,0.75); font-size: 14px; font-weight: 500; text-decoration: none; padding-bottom: 3px; position: relative; transition: color .2s; }
  .nav-link::after { content:''; position:absolute; bottom:0; left:0; width:0; height:1.5px; background:${C.gold}; transition: width .25s; }
  .nav-link:hover { color: #fff; }
  .nav-link:hover::after { width: 100%; }
  .nav-link.active { color: #fff; }
  .nav-link.active::after { width: 100%; }
  .info-card { transition: transform .2s, box-shadow .2s; }
  .info-card:hover { transform: translateY(-2px); box-shadow: 0 10px 36px rgba(0,77,43,0.12) !important; }
  input:focus, textarea:focus { border-color: ${C.greenMid} !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(0,107,60,0.1) !important; outline: none; }
  .send-btn:hover { background: #005530 !important; box-shadow: 0 8px 28px rgba(0,77,43,0.35) !important; }
  .send-btn:active { transform: scale(0.98); }
  .reset-btn:hover { background: #D0EAD9 !important; }
  .mobile-menu-link { color: rgba(255,255,255,0.85); font-size: 15px; font-weight: 500; text-decoration: none; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.1); display: block; }
  @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
  .mobile-menu { animation: slideDown .25s ease; }
`;

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

const HERO_PATTERN = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E")`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent]   = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isSmall  = w < 480;

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSend = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Merci de remplir votre nom, votre email et votre message.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setError("Une erreur est survenue lors de l'envoi. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      {/* NAV */}
      <PublicNav />

      {/* HERO */}
      <div style={{
        background: "linear-gradient(150deg, #002D1A 0%, #004D2B 50%, #006B3C 100%)",
        padding: isMobile ? "52px 20px 48px" : "72px 40px 64px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:HERO_PATTERN }} />
        <div style={{ position:"relative" }}>
          <span style={{ display:"inline-block", fontSize:11, fontWeight:600, letterSpacing:"2.5px", textTransform:"uppercase", color:C.gold, background:"rgba(184,147,58,0.15)", border:"1px solid rgba(184,147,58,0.3)", borderRadius:20, padding:"5px 14px", marginBottom:16 }}>
            Support client
          </span>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontWeight:500, fontSize: isSmall ? 32 : isMobile ? 40 : 52, color:"#fff", lineHeight:1.15, margin:"0 0 14px" }}>
            Contactez<br/><em style={{ fontStyle:"italic", color:"#A8D5BC" }}>notre équipe</em>
          </h1>
          <p style={{ color:"rgba(255,255,255,0.68)", fontSize:15, fontWeight:300, maxWidth:400, margin:"0 auto" }}>
            Nous sommes disponibles pour répondre à toutes vos questions sur le réseau Transgabonais.
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{
        maxWidth: 940,
        margin: "0 auto",
        padding: isMobile ? "36px 16px 60px" : "56px 24px 80px",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
        gap: isMobile ? 28 : 40,
        alignItems: "start",
      }}>

        {/* ── LEFT: Info ── */}
        <div>
          <div style={{ fontSize:11, fontWeight:600, letterSpacing:"2px", textTransform:"uppercase", color:C.gold, marginBottom:6 }}>Où nous trouver</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize: isMobile ? 24 : 28, fontWeight:500, color:C.text, marginBottom:28, lineHeight:1.3 }}>
            Informations<br/>de contact
          </div>

          {[
            { icon:"📍", title:"Siège social",   desc:"Libreville, Gabon\nBP XXXX - Owendo" },
            { icon:"📞", title:"Téléphone",       desc:"+241 01 XX XX XX\nDu lundi au vendredi, 7h – 19h" },
            { icon:"✉️", title:"Email",           desc:"contact@setrag.ga\nRéponse sous 24 heures" },
            { icon:"🚆", title:"Gare principale", desc:"Gare d'Owendo\nOwendo, Libreville" },
          ].map((item, i) => (
            <div key={item.title} className="info-card" style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              padding: isSmall ? "14px 16px" : "18px 20px",
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              marginBottom: 14,
              animation: `fadeUp .5s ease ${i * 0.07}s both`,
            }}>
              <div style={{ width: isSmall ? 40 : 46, height: isSmall ? 40 : 46, borderRadius:12, background:C.greenLt, display:"flex", alignItems:"center", justifyContent:"center", fontSize: isSmall ? 17 : 20, flexShrink:0 }}>
                {item.icon}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:4 }}>{item.title}</div>
                <div style={{ fontSize:13, color:C.sub, lineHeight:1.6, whiteSpace:"pre-line" }}>{item.desc}</div>
              </div>
            </div>
          ))}

          <div style={{ background:`linear-gradient(135deg, ${C.green}, ${C.greenMid})`, borderRadius:14, padding:"16px 20px", marginTop:18, display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:"#4ADE80", boxShadow:"0 0 0 3px rgba(74,222,128,0.25)", flexShrink:0 }} />
            <p style={{ fontSize:13, color:"rgba(255,255,255,0.85)", margin:0 }}>
              <strong style={{ color:"#fff" }}>Service ouvert</strong> · Lun – Ven, 7h00 – 19h00
            </p>
          </div>
        </div>

        {/* ── RIGHT: Form ── */}
        <div style={{
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          padding: isMobile ? "24px 20px" : "36px 32px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.06)",
          animation: "fadeUp .5s ease .15s both",
        }}>
          {sent ? (
            <div style={{ textAlign:"center", padding:"48px 20px", animation:"fadeUp .4s ease" }}>
              <div style={{ width:72, height:72, borderRadius:"50%", background:C.greenLt, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, margin:"0 auto 20px", border:"2px solid rgba(0,107,60,0.2)" }}>✅</div>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:500, color:C.green, marginBottom:8 }}>Message envoyé !</h3>
              <p style={{ fontSize:14, color:C.sub, margin:0 }}>Notre équipe vous répondra dans les meilleurs délais.</p>
              <button className="reset-btn" onClick={() => setSent(false)} style={{ display:"inline-block", marginTop:20, padding:"10px 24px", background:C.greenLt, color:C.green, border:`1px solid rgba(0,107,60,0.2)`, borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:500, color:C.text, marginBottom:24 }}>Envoyer un message</div>

              <div style={{ display:"grid", gridTemplateColumns: isSmall ? "1fr" : "1fr 1fr", gap:14, marginBottom:0 }}>
                {[
                  { key:"name",  label:"Nom complet", placeholder:"Jean-Pierre Moussavou", type:"text" },
                  { key:"email", label:"Email",        placeholder:"votre@email.ga",        type:"email" },
                ].map(f => (
                  <Field key={f.key} {...f} value={form[f.key]} onChange={update(f.key)} />
                ))}
              </div>
              <Field label="Téléphone (pour vous rappeler ou vous écrire sur WhatsApp)" placeholder="074 00 00 00" type="tel" value={form.phone} onChange={update("phone")} />
              <Field key="subject" label="Objet" placeholder="Demande d'information sur les billets" type="text" value={form.subject} onChange={update("subject")} />
              <div style={{ marginBottom:20 }}>
                <label style={labelStyle}>Message</label>
                <textarea placeholder="Décrivez votre demande en détail…" value={form.message} onChange={update("message")}
                  style={{ ...inputStyle, height:110, resize:"vertical" }} />
              </div>
              {error && <InlineAlert type="error">{error}</InlineAlert>}
              <button className="send-btn" onClick={handleSend} disabled={sending} style={{ width:"100%", background:C.green, color:"#fff", border:"none", borderRadius:12, padding:14, fontFamily:"'DM Sans',sans-serif", fontSize:14, fontWeight:600, letterSpacing:"0.5px", cursor:"pointer", transition:"background .2s, box-shadow .2s", position:"relative", overflow:"hidden", opacity:sending?0.7:1 }}>
                {sending ? "Envoi en cours…" : "Envoyer le message →"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background:C.green, color:"rgba(255,255,255,0.6)", textAlign:"center", padding:24, fontSize:12.5 }}>
        © 2024 <strong style={{ color:"rgba(255,255,255,0.9)" }}>SETRAG</strong> - Société d'Exploitation du Transgabonais · Tous droits réservés
      </footer>
    </>
  );
}

function Field({ label, placeholder, type, value, onChange }) {
  return (
    <div style={{ marginBottom:18 }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} style={inputStyle} />
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: "#6B6B6B",
  marginBottom: 7,
};

const inputStyle = {
  width: "100%",
  border: "1.5px solid #E2DDD7",
  borderRadius: 10,
  padding: "12px 14px",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  color: "#1A1A1A",
  background: "#F9F6F1",
  outline: "none",
  transition: "border-color .2s, background .2s, box-shadow .2s",
  display: "block",
};